/**
 * The `SurfaceHost` the loader installs into WS-2's runtime (layered shell surfaces, D13, D18,
 * D28, R-4, R-8, R-9, R-43, R-44, R-47, R-53, R-102, R-129, WS-6 §3.3).
 *
 * It resolves a target against the principal's registry with the permission check, adapts a
 * legacy bundle through WS-3's adapter, mints each mount's services through WS-8's provider and
 * releases them. It builds no URL of its own: a declared ref carries the URLs of the row's bundle
 * for the declaration's `uiPackage` (R-102), the system ref carries WS-7's `/system/` URLs, and
 * the interim root carries the URL the build wrote into `index.html`. Every registry app mounts
 * on its declared-surface path: core-api gives an app that declares only `shell[]` one routed
 * surface, `legacy.v1` (R-129), and a module of that surface with no `mount` still goes to
 * `adaptModule`.
 */
import { EXTENSION_POINT_ID_MAX_LENGTH, EXTENSION_POINT_ID_PATTERN, hasPermission } from '@zion/sdk-contracts';
import type { SurfaceDeclaration } from '@zion/sdk-contracts';
import type {
  DeclaredSurfaceRef,
  ExtensionImplementer,
  LegacySurfaceRef,
  RuntimeEvent,
  SurfaceHost,
  SurfaceMountPlacement,
  SurfaceRef,
  SurfaceServices,
  SurfaceServicesProvider,
  SurfaceTarget,
} from '@zion/sdk-surfaces/v1';
import type * as SurfacesReactV1 from '@zion/sdk-surfaces-react/v1';
import {
  SYSTEM_OUTPUT_NAMES,
  SYSTEM_STYLESHEET_URL,
  SYSTEM_SURFACE_URL,
} from '@zion/shell-system-surfaces/protocol';
import type { AppBundle, AppRegistryEntry } from '../api/types.js';
import { buildAppList } from '../logic/app-loader-utils.js';
import type { ServiceSession } from '@zion/surface-host';
import { DEFAULT_ROOT_APP_ID } from './root-resolution.js';

/** The `appId` every system mount carries (D18). No registry row named `system` ever resolves. */
export const SYSTEM_APP_ID = 'system';

/** The one surface the system bundle declares; its input's `surface` names the page. */
export const SYSTEM_SURFACE = 'system.v1';

/** The workspace package that builds the system bundle. */
export const SYSTEM_UI_PACKAGE = '@zion/shell-system-surfaces';

/** The target the loader mounts every system page through. */
export const SYSTEM_TARGET: SurfaceTarget = Object.freeze({ app: SYSTEM_APP_ID, surface: SYSTEM_SURFACE });

/** The stage-2 interim root: today's chrome, home and `AppHost` as one legacy bundle (D28, R-15). */
export const INTERIM_ROOT_TARGET: SurfaceTarget = Object.freeze({ app: DEFAULT_ROOT_APP_ID, surface: null });

/** WS-3's two legacy functions, imported by the map's URL for `@zion/sdk-surfaces-react/v1`. */
export type LegacyAdapter = Pick<typeof SurfacesReactV1, 'isLegacyAppModule' | 'adaptLegacyApp'>;

/** The interim root's bundle and stylesheet, from the `zion-interim-root` meta tags the build writes. */
export interface InterimRootAsset {
  readonly bundleUrl: string;
  readonly cssUrl: string;
}

/** What the host reads. Every member is read when a call needs it, so a later registry or adapter shows. */
export interface LoaderHostInputs {
  /** The principal's registry from `GET /v1/apps`; `[]` until it arrives. */
  readonly registry: () => readonly AppRegistryEntry[];
  /** WS-8's session port, for the permission check and the legacy adapter's `orgId`. */
  readonly session: ServiceSession;
  /** WS-8's per-mount services boundary. */
  readonly provider: SurfaceServicesProvider;
  /** The React adapter once imported, else `null`. */
  readonly legacyAdapter: () => LegacyAdapter | null;
  /** The interim root, or `null` when the build wrote none. */
  readonly interimRoot: InterimRootAsset | null;
  /** The shell's version, from the `zion-shell-version` meta tag; the system ref's `version`. */
  readonly shellVersion: string;
  /** Receives every runtime event of every mount. */
  readonly onRuntimeEvent: (event: RuntimeEvent) => void;
}

/** The installed host, plus the loader's read of one mount's live services. */
export interface LoaderHost extends SurfaceHost {
  /** The services minted for `mountId`, or `null` before the mint and after the release. */
  servicesOf(mountId: string): SurfaceServices | null;
}

/** The system bundle's declaration: one embedded surface, an open input and one open output per protocol output. */
const SYSTEM_DECLARATION: SurfaceDeclaration = Object.freeze({
  name: SYSTEM_SURFACE,
  kind: 'embedded',
  uiPackage: SYSTEM_UI_PACKAGE,
  input: Object.freeze({}),
  outputs: Object.freeze(Object.fromEntries(SYSTEM_OUTPUT_NAMES.map((name) => [name, Object.freeze({})]))),
});

/**
 * True when `url` resolves against the document to the document's own origin (R-44 as read by
 * R-53). A missing location, a missing base or a URL that does not parse answers false.
 */
function isSameOriginWithDocument(url: string): boolean {
  const doc = globalThis.document as Document | undefined;
  const origin = doc?.location?.origin;
  if (typeof origin !== 'string' || origin === '' || origin === 'null') return false;
  try {
    return new URL(url, doc?.baseURI).origin === origin;
  } catch {
    return false;
  }
}

/**
 * The bundle of `row` for one of its declared surfaces (layered shell surfaces, R-1, R-102,
 * R-129): `row.bundles[declaration.uiPackage]` when the map has an own entry for that
 * `uiPackage`, else `null`.
 *
 * The lookup reads own keys only (`Object.hasOwn`), so a `uiPackage` such as `constructor` never
 * reads `Object.prototype`. An absent map reads as a map with no entries. The answer never comes
 * from another package's entry or from a row-level URL: the row carries none (R-129). A `null`
 * answer is the fail-closed direction.
 *
 * It checks no origin and no permission, and writes no line; `resolve` does all three. Pure.
 * Callers: `resolve` below, and `boot.ts` before it mounts the root, where a `null` is
 * `fatal: manifest`.
 */
export function surfaceBundleOf(row: AppRegistryEntry, declaration: SurfaceDeclaration): AppBundle | null {
  const { bundles } = row;
  return bundles !== undefined && Object.hasOwn(bundles, declaration.uiPackage) ? bundles[declaration.uiPackage] : null;
}

/**
 * The one console line for a declared surface for which {@link surfaceBundleOf} answers `null`
 * (R-102): the row's map has no entry for its `uiPackage`. It names the app slug, the surface and
 * the package, which the authed registry supplied; it names no URL.
 */
export function bundleMissingMessage(appId: string, declaration: SurfaceDeclaration): string {
  return `[shell] surface ${appId}/${declaration.name} names uiPackage ${declaration.uiPackage}, and the registry row carries no bundle for it (R-102)`;
}

function urlsAreSameOrigin(bundleUrl: string, cssUrl: string | null): boolean {
  return isSameOriginWithDocument(bundleUrl) && (cssUrl === null || isSameOriginWithDocument(cssUrl));
}

const POINT_ID = new RegExp(EXTENSION_POINT_ID_PATTERN);

/** True for an extension point id: a string of at most 96 characters that matches the grammar. Pure. */
function isPointId(value: unknown): value is string {
  return typeof value === 'string' && value.length <= EXTENSION_POINT_ID_MAX_LENGTH && POINT_ID.test(value);
}

/** True when `decl` is an embedded, non-root surface whose `implements` lists `point`. Pure. */
function declaresImplementer(decl: SurfaceDeclaration, point: string): boolean {
  return decl.kind === 'embedded' && decl.root !== true && Array.isArray(decl.implements) && decl.implements.includes(point);
}

/** Code-unit order of two strings: negative, zero or positive. */
function compareCodeUnits(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * Build the host. `installSurfaceHost(createLoaderHost(...))` is the loader's one install.
 *
 * `resolve(target)` answers in this order:
 * 1. `SYSTEM_TARGET`: the system ref (`appId` `system`, the shell's version, WS-7's
 *    `SYSTEM_SURFACE_URL` and `SYSTEM_STYLESHEET_URL`, `appSurfaceCount` 1). Any other surface on
 *    app `system`: `null`.
 * 2. A registry row whose `appId` matches the target.
 *    - `surface: null`: `null`. A registry row has no legacy ref; an app that declares only
 *      `shell[]` resolves by its `legacy.v1` surface (R-129).
 *    - A name: the row's declaration of that name with `appSurfaceCount` the row's `surfaces[]`
 *      length, or `null` when it declares none. A declaration whose `requiresPermission` the
 *      session's `orgPermissions` fail under `hasPermission` answers `null` for every kind;
 *      anonymous holds none (R-9). The ref's two URLs are {@link surfaceBundleOf}'s answer, the
 *      row's bundle for the declaration's `uiPackage` (R-102). A `null` answer makes `resolve`
 *      answer `null`, so the runtime fails the mount `unresolved`, and the host logs
 *      {@link bundleMissingMessage} once per such resolve with `console.warn`.
 * 3. `INTERIM_ROOT_TARGET` with no `default-shell` row: a legacy ref over the interim root's
 *    bundle with `cssUrl: null` (the loader links that stylesheet unscoped), or `null` without one.
 * Every ref whose bundle or stylesheet URL is not same-origin with the document answers `null`
 * (R-44, R-53). A point target `{ point, app? }`, and any target without an own `surface` key,
 * answers `null`: the runtime turns a point target into the app arm of the implementer it picked
 * from `implementers` before it calls `resolve` (surface extension points, D9). Never rejects.
 *
 * `implementers(point)` (surface extension points, D9, D10, WS-7) answers the implementers of
 * `point` that `resolve` would answer a ref for: each `{ app, surface }` where the first registry
 * row of `app` has, as the first declaration of the name `surface`, an embedded declaration with no
 * `root: true` whose `implements` lists `point`, and where `resolve({ app, surface })` passes its
 * checks: the session's `orgPermissions` meet `requiresPermission`, {@link surfaceBundleOf} answers
 * a bundle, and its bundle and stylesheet URLs are same-origin with the document. A row whose
 * `appId` is `system` never answers, as it never resolves. The answer is a new frozen array of
 * frozen `{ app, surface }` objects, one per pair, sorted by app, then by surface, by code unit. A
 * point id that is not a string of at most 96 characters matching `EXTENSION_POINT_ID_PATTERN`, an
 * unknown point and an empty registry answer `[]`. Pure read: it writes no console line, also for a
 * missing bundle, and reads the registry and the session at each call. The kernel guard is the
 * boot's: it wraps this member in the `on` arm (`kernelGuardedHost` in `boot.ts`).
 *
 * `adaptModule(mod, ref)` answers `null` while no adapter is loaded or `isLegacyAppModule`
 * refuses the namespace (R-47), else `adaptLegacyApp(mod, { appId, orgId, fixtures })`, whose
 * throw propagates to the runtime.
 *
 * `servicesFor(ref, mountId, element)` (R-43) builds WS-8's `SurfaceMountRequest`: placement
 * `system` for the system ref; a routed root at `/` for a ref with no declaration, which only the
 * interim root is; else the declaration's kind, `root` and `mountPath` (`/` for a root, the row's
 * `manifest.mountPath` for a routed declaration with none, else `/`). `appUuid` and `permissionKeys` are the row's, else `null` and `[]`.
 * It calls `provider.servicesFor` and keeps the result for `servicesOf`; a provider throw
 * propagates. `releaseServices(mountId)` calls `provider.releaseServices` and forgets the mount.
 * `report(event)` hands the event to `onRuntimeEvent`.
 */
export function createLoaderHost(inputs: LoaderHostInputs): LoaderHost {
  const live = new Map<string, SurfaceServices>();

  const systemRef = (): DeclaredSurfaceRef =>
    Object.freeze({
      appId: SYSTEM_APP_ID,
      version: inputs.shellVersion,
      bundleUrl: SYSTEM_SURFACE_URL,
      cssUrl: SYSTEM_STYLESHEET_URL,
      declaration: SYSTEM_DECLARATION,
      appSurfaceCount: 1,
    });

  const permitted = (decl: SurfaceDeclaration): boolean =>
    decl.requiresPermission === undefined || hasPermission(inputs.session.current().orgPermissions, decl.requiresPermission);

  const resolveSync = (target: SurfaceTarget): SurfaceRef | null => {
    // Only the app arm resolves. A point target, or a value with both arms, is refused (F3).
    if ('point' in target || !Object.hasOwn(target, 'surface')) return null;
    if (target.app === SYSTEM_APP_ID) return target.surface === SYSTEM_SURFACE ? systemRef() : null;
    const row = inputs.registry().find((entry) => entry.appId === target.app);
    if (row === undefined) {
      if (target.app !== INTERIM_ROOT_TARGET.app || target.surface !== null || inputs.interimRoot === null) return null;
      const interim: LegacySurfaceRef = Object.freeze({
        appId: DEFAULT_ROOT_APP_ID,
        version: inputs.shellVersion,
        bundleUrl: inputs.interimRoot.bundleUrl,
        cssUrl: null,
        declaration: null,
      });
      return interim;
    }
    if (target.surface === null) return null;
    const decl = row.surfaces.find((d) => d.name === target.surface);
    if (decl === undefined || !permitted(decl)) return null;
    const bundle = surfaceBundleOf(row, decl);
    if (bundle === null) {
      console.warn(bundleMissingMessage(row.appId, decl));
      return null;
    }
    const declared: DeclaredSurfaceRef = Object.freeze({
      appId: row.appId,
      version: row.version,
      bundleUrl: bundle.bundleUrl,
      cssUrl: bundle.cssUrl,
      declaration: decl,
      appSurfaceCount: row.surfaces.length,
    });
    return declared;
  };

  const placementOf = (ref: SurfaceRef, row: AppRegistryEntry | undefined): SurfaceMountPlacement => {
    if (ref.appId === SYSTEM_APP_ID) return { kind: 'system' };
    const decl = ref.declaration;
    if (decl === null) return { kind: 'routed', root: true, mountPath: '/' };
    if (decl.kind === 'embedded') return { kind: 'embedded' };
    const root = decl.root === true;
    return { kind: 'routed', root, mountPath: root ? '/' : (decl.mountPath ?? row?.manifest.mountPath ?? '/') };
  };

  const implementersOf = (point: string): readonly ExtensionImplementer[] => {
    if (!isPointId(point)) return Object.freeze([]);
    const rows = inputs.registry();
    const found: ExtensionImplementer[] = [];
    for (const row of rows) {
      // `resolve` reads the first row of an app and the first declaration of a name, so a later
      // twin never mounts and never answers here. That also keeps one entry per app and surface.
      if (row.appId === SYSTEM_APP_ID || rows.find((r) => r.appId === row.appId) !== row) continue;
      for (const decl of row.surfaces) {
        if (row.surfaces.find((d) => d.name === decl.name) !== decl) continue;
        if (!declaresImplementer(decl, point) || !permitted(decl)) continue;
        const bundle = surfaceBundleOf(row, decl);
        if (bundle === null || !urlsAreSameOrigin(bundle.bundleUrl, bundle.cssUrl)) continue;
        found.push(Object.freeze({ app: row.appId, surface: decl.name }));
      }
    }
    found.sort((a, b) => compareCodeUnits(a.app, b.app) || compareCodeUnits(a.surface, b.surface));
    return Object.freeze(found);
  };

  return {
    async resolve(target) {
      const ref = resolveSync(target);
      if (ref === null || !urlsAreSameOrigin(ref.bundleUrl, ref.cssUrl)) return null;
      return ref;
    },
    implementers(point) {
      return implementersOf(point);
    },
    adaptModule(mod, ref) {
      const adapter = inputs.legacyAdapter();
      if (adapter === null || !adapter.isLegacyAppModule(mod)) return null;
      return adapter.adaptLegacyApp(mod, {
        appId: ref.appId,
        orgId: inputs.session.current().orgId,
        fixtures: buildAppList(inputs.registry()),
      });
    },
    servicesFor(ref, mountId, element) {
      const system = ref.appId === SYSTEM_APP_ID;
      const row = system ? undefined : inputs.registry().find((entry) => entry.appId === ref.appId);
      const services = inputs.provider.servicesFor({
        mountId,
        appId: ref.appId,
        appUuid: row?.appUuid ?? null,
        surface: ref.declaration?.name ?? null,
        permissionKeys: row?.permissionKeys ?? [],
        element,
        placement: placementOf(ref, row),
      });
      live.set(mountId, services);
      return services;
    },
    releaseServices(mountId) {
      live.delete(mountId);
      inputs.provider.releaseServices(mountId);
    },
    report(event) {
      inputs.onRuntimeEvent(event);
    },
    servicesOf(mountId) {
      return live.get(mountId) ?? null;
    },
  };
}
